import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Typography,
    Stack,
    Icon,
} from '../../../../base';
import { IClientTimeToHireCompanyData } from '../../../../common';
import {
    StyledStatsRow,
    StyledStatItem,
    StyledIconCircle,
    StyledVerticalDivider,
} from './SummaryStatsBar.styles';

interface ISummaryStatsBarProps {
    data: IClientTimeToHireCompanyData;
}

const SummaryStatsBar = ({ data }: ISummaryStatsBarProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const primaryGradient = `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 86.24%)`;

    const totalCandidates =
        (data.unprocessedToDeclined?.count || 0) +
        (data.unprocessedToInReview?.count || 0) +
        (data.timeInReview?.count || 0) +
        (data.hiringTimeAverage?.count || 0) +
        (data.timeToFirstApplication?.count || 0);

    const stats = [
        {
            icon: 'PersonOutlinedIcon',
            gradient: primaryGradient,
            label: t('reportsPage.timeToHireReport.candidateResponseSchedule'),
            value: `${data.timeInReview?.averageDays !== null ? Math.round(data.timeInReview.averageDays) : 0} ${t('reportsPage.timeToHireReport.days')}`,
        },
        {
            icon: 'MailOutlineIcon',
            gradient: primaryGradient,
            label: t('reportsPage.timeToHireReport.candidateResponseEmail'),
            value: `${data.unprocessedToInReview?.averageDays !== null ? Math.round(data.unprocessedToInReview.averageDays) : 0} ${t('reportsPage.timeToHireReport.days')}`,
        },
        {
            icon: 'PeopleOutlineOutlinedIcon',
            gradient: primaryGradient,
            label: t('reportsPage.timeToHireReport.nominationsTwoWeekDelay'),
            value: `${data.currentlyInNew?.count || 0} ${t('reportsPage.timeToHireReport.candidates')}`,
        },
        {
            icon: 'PeopleOutlineOutlinedIcon',
            gradient: primaryGradient,
            label: t('reportsPage.timeToHireReport.totalCandidates'),
            value: `${totalCandidates} ${t('reportsPage.timeToHireReport.candidates')}`,
        },
    ];

    return (
        <StyledStatsRow>
            {stats.map((stat, index) => (
                <Fragment key={stat.label}>
                    <StyledStatItem>
                        <StyledIconCircle gradient={stat.gradient}>
                            <Icon
                                name={stat.icon}
                                size={24}
                                style={{ color: theme.palette.common.white }}
                            />
                        </StyledIconCircle>
                        <Stack>
                            <Typography
                                variant="body2"
                                color={theme.palette.grey[500]}
                            >
                                {stat.label}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 700 }}
                            >
                                {stat.value}
                            </Typography>
                        </Stack>
                    </StyledStatItem>
                    {index < stats.length - 1 && <StyledVerticalDivider />}
                </Fragment>
            ))}
        </StyledStatsRow>
    );
};

export default SummaryStatsBar;
